import * as chrono from 'chrono-node'
import DateInput from 'components/DateInput'
import GenericServerError from 'components/GenericServerError'
import { parseHtml } from 'components/HtmlContent'
import PhotoUploadField from 'components/PhotoUploadField'
import RadioInput from 'components/RadioInput'
import { KitRegistrationInfo } from 'components/Registration/KitIdForm'
import SelectField from 'components/SelectField'
import SubmitButton from 'components/SubmitButton'
import TextInput from 'components/TextInput'
import ethnicities from 'data/ethnicities'
import {
  differenceInWeeks,
  differenceInYears,
  isBefore,
  isValid,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
} from 'date-fns/fp'
import { ErrorMessage, Field, Form, Formik, FormikProps, useFormikContext } from 'formik'
import { toFriendlyKitId } from 'helpers/string'
import { isEmpty } from 'lodash'
import { flow, range } from 'lodash/fp'
import * as mimeMatch from 'mime-match'
import { FaExclamationTriangle } from 'react-icons/fa'
import { chronoDate } from 'schema/date'
import * as yup from 'yup'
import { NcovTestPurpose } from '../../../__generated__/globalTypes'
import AntigenResultInputField from './AntigenResultInputField'

const casualGbChrono = new chrono.Chrono(chrono.en.createCasualConfiguration(true))

const getSampleDateTime = (values: { sampleDate: string; sampleTimeHours: number; sampleTimeMinutes: number }): Date =>
  flow([
    (s: string) => casualGbChrono.parseDate(s),
    setHours(values.sampleTimeHours),
    setMinutes(values.sampleTimeMinutes),
    setSeconds(0),
    setMilliseconds(0),
  ])(values.sampleDate)

const validationSchema = (isThirdParty: boolean, isForRelease: boolean, sku?: string) =>
  yup.object().shape({
    firstName: yup
      .string()
      .label('First Name')
      .required()
      .matches(/^[0-9a-zA-Z -]*$/, {
        excludeEmptyString: true,
        message: 'Your name can only include letters, number, spaces and hyphens',
      }),
    lastName: yup
      .string()
      .label('Last Name')
      .required()
      .required()
      .matches(/^[0-9a-zA-Z -]*$/, {
        excludeEmptyString: true,
        message: 'Your name can only include letters, number, spaces and hyphens',
      }),
    email: yup.string().label('Email').email().required(),
    emailConfirm: yup
      .string()
      .label('Confirm Email')
      .email()
      .test('matches-email', 'Both email inputs must match', (value, { parent }) => {
        return value?.trim().toUpperCase() === parent.email?.trim().toUpperCase()
      }),
    dob: chronoDate(chrono.en.GB)
      .label('Date of Birth')
      .typeError('Not a valid date')
      .required()
      .test('is-reasonable-age', 'Date of Birth must be between 1 week and 150 years old', (value) => {
        try {
          const now = new Date()
          return differenceInWeeks(value, now) > 1 && differenceInYears(value, now) < 150
        } catch (err) {
          return true
        }
      }),
    passportNumber: yup
      .string()
      .when('isForTravel', (isForTravel, schema) =>
        isForTravel === 'Yes'
          ? schema.required('You must supply a passport number if you are using the certificate for travel purposes')
          : schema
      )
      .label('Passport /ID Number'),
    sampleDate: chronoDate(casualGbChrono).label('Sample Date').typeError('Not a valid date').required(),
    // Synthetic field for validation
    sampleDateTime: yup
      .string()
      .test('not-in-the-future', 'Your sample date & time cannot be in the future', (value, context) => {
        try {
          const sampleDateTime = getSampleDateTime(context.from[0].value)

          if (!isValid(sampleDateTime)) {
            return true
          }

          return isBefore(new Date(), sampleDateTime)
        } catch (err) {
          return true
        }
      }),
    sharingConsent:
      isThirdParty &&
      yup
        .mixed()
        .oneOf(['true', 'false'])
        .required('Please indicate if you do or do not consent to sharing your result with your employer.'),

    ...(isForRelease
      ? {
          ttr: yup.object().shape({
            arrivalDate: chronoDate(casualGbChrono).label('Arrival Date').typeError('Not a valid date').required(),
            departureDate: chronoDate(casualGbChrono).label('Departure Date').typeError('Not a valid date').required(),
            uniqueId: yup
              .string()
              .label('Day 2/8 Test Booking Reference Number')
              .matches(/.{5}\d{7}/, 'Must be 12 characters in total: 5 letters followed by 7 numbers.'),
          }),
        }
      : {
          ttr: yup.object().shape({
            homeAddress: yup.object().shape({
              location: yup
                .string()
                .label('Location')
                .when((location, schema) => {
                  return ['rapid_travel', 'rapid_travel_hughes'].includes(sku)
                    ? schema.required('You must specify if you are in the United Kingdom')
                    : schema
                }),
              postcode: yup
                .string()
                .label('Post Code')
                .when('location', (location, schema) =>
                  location === 'yes'
                    ? schema
                        .required()
                        .matches(
                          /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})$/,
                          {
                            excludeEmptyString: false,
                            message: 'invalid post code',
                          }
                        )
                    : schema
                ),
            }),
          }),
        }),
  })

function validateRequired(value, ...rest) {
  if (isEmpty(value)) {
    return 'Required'
  }

  return undefined
}

const validateRequiredWithMessage = (message: string) => (value) => {
  if (isEmpty(value)) {
    return message
  }

  return undefined
}

const getTimePartsNow = () => {
  const now = new Date()

  const hours = now.getHours()

  return {
    sampleTimeHours: hours,
    sampleTimeMinutes: now.getMinutes(),
  }
}

export const getInitialValues = (sku: string) => ({
  firstName: '',
  lastName: '',
  dob: '',
  sex: '',
  nationality: '',
  email: '',
  sharingConsent: '',
  passportNumber: '',
  trackingNumber: '',
  sampleDate: 'Today',
  ...getTimePartsNow(),
  isForTravel: ['rapid_travel', 'rapid_travel_hughes'].includes(sku) ? 'Yes' : 'No',
  result: null,
  photoFile: null,
  rapidTravelConfirmation: false,
  ttr: {
    sex: '',
    uniqueId: '',
    nhsNumber: '',
    phoneNumber: '',
    ethnicity: '',
    arrivalDate: '',
    departureDate: '',
    flightNumber: '',
    originatingCountry: '',
    transitCountries: '',
    homeAddress: {
      address1: '',
      address2: '',
      town: '',
      postcode: '',
    },
    selfIsolationAddress: {
      address1: '',
      address2: '',
      town: '',
      postcode: '',
    },
    selfIsolatingAtHome: true,
    // For Day 2 and 8
    vaccinationStatus: null,
    vaccinationProvider: '',
    day2or8: '',
  },
  // Partial repeat for rapid_multi registration
  sex: '',
  ethnicity: '',
  homeAddress: {
    address1: '',
    address2: '',
    town: '',
    postcode: '',
  },

  _form: '',
})

const AddressForm = ({ name, labelPrefix }: { name: string; labelPrefix?: string }) => (
  <>
    <TextInput
      name={`${name}.address1`}
      label={`${labelPrefix} Address`}
      placeholder={`${labelPrefix} Address Line 1`}
      type="text"
      validate={validateRequired}
      autoComplete="off"
    />
    <TextInput name={`${name}.address2`} placeholder="Address Line 2" type="text" autoComplete="off" />
    <TextInput
      name={`${name}.town`}
      label={`${labelPrefix} Town`}
      type="text"
      validate={validateRequired}
      autoComplete="off"
    />
    <TextInput
      name={`${name}.postcode`}
      label={`${labelPrefix} Postcode`}
      type="text"
      validate={validateRequired}
      autoComplete="off"
    />
  </>
)

const ReleaseInfoSection = ({ values }: { values: { ttr: { selfIsolatingAtHome: boolean } } }) => (
  <>
    <RadioInput
      labelClassName="label is-size-7 has-text-weight-light"
      name="ttr.sex"
      options={['Male', 'Female']}
      validate={validateRequired}
    >
      Sex
    </RadioInput>
    <TextInput name="ttr.nhsNumber" label="NHS Number (if known)" placeholder="NHS Number" autoComplete="off" />
    <TextInput
      name={
        /* Not using prefix because we'll treat this like a regular
               passport number */
        'passportNumber'
      }
      label="Passport / ID Number"
      type="text"
      validate={validateRequired}
      autoComplete="off"
    />
    <TextInput name="ttr.phoneNumber" label="Phone Number" validate={validateRequired} autoComplete="off" />
    <SelectField name="ttr.ethnicity" label={<p className="is-size-7">Ethnicity</p>} validate={validateRequired}>
      <option disabled value="">
        Select Ethnicity
      </option>

      {ethnicities.map((e) => (
        <option key="e">{e}</option>
      ))}
    </SelectField>

    <DateInput
      name="ttr.arrivalDate"
      chronoInstance={casualGbChrono}
      label={
        <>
          <span>Date of arrival in the UK </span>
          <span className="has-text-weight-light has-text-info">(e.g. 25/06/2021)</span>
        </>
      }
    />

    <DateInput
      name="ttr.departureDate"
      chronoInstance={casualGbChrono}
      label={
        <>
          <span>Date of departure from origin country to travel to the UK </span>
          <span className="has-text-weight-light has-text-info">(e.g. 25/06/2021)</span>
        </>
      }
    />

    <TextInput
      name="ttr.flightNumber"
      label="Flight number, coach number, or vessel name"
      validate={validateRequired}
      autoComplete="off"
    />
    <TextInput
      name="ttr.originatingCountry"
      label="Country or territory you travelled from"
      placeholder="e.g. Spain"
      validate={validateRequired}
      autoComplete="off"
    />
    <TextInput
      name="ttr.transitCountries"
      label="Other countries or territories transited through on this journey"
      placeholder="e.g. Switzerland, France"
      autoComplete="off"
    />

    <AddressForm name="ttr.homeAddress" labelPrefix="Home" />

    <label className="checkbox">
      <Field name="ttr.selfIsolatingAtHome" type="checkbox" /> I intend to reside at my home address above while in the
      UK
    </label>

    {values.ttr.selfIsolatingAtHome ? null : <AddressForm name="ttr.selfIsolationAddress" labelPrefix="Residence" />}
  </>
)

const MicrosoftEmailWarning = () => {
  const formik = useFormikContext<{ email: string }>()

  const domains = ['btinternet.com', 'live.co.uk', 'hotmail.co.uk', 'live.com', 'hotmail.com']

  const domain = domains.find((x) => formik.values.email.toLowerCase().endsWith(x))

  if (!domain) return null

  return (
    <article className="message is-warning">
      <div className="message-body">
        Some email accounts (e.g. btinternet, hotmail and live) may reject emails from unknown contacts to prevent you
        receiving spam. We <b>strongly recommend</b> adding our sending email address - "no-reply@c19testing.co.uk" - to
        your{' '}
        <a href="https://www.bt.com/help/email/using-bt-mail-s-anti-spam-features" target="_blank" rel="noreferrer">
          'Safe Senders' or 'Allowed' list
        </a>{' '}
        to ensure you receive your result. Alternatively, you could use a different email address.
      </div>
    </article>
  )
}

const hasErrors = (formik: FormikProps<any>) => {
  return formik.submitCount > 0 && Object.keys(formik.errors).length > 0
}

interface DetailsFormProps {
  registrationInfo: KitRegistrationInfo
  thirdPartyAssociateId: string
  consentText: string
  testPurpose?: NcovTestPurpose
  initialValues: any
  onComplete: (values: any) => void
}

const DetailsForm = ({ registrationInfo, testPurpose, initialValues, onComplete }: DetailsFormProps) => {
  // rapid_travel is _always_ for travel so don't give the option
  // isForTravel is set to yes in initialValues

  const showTracking = [
    'ncov',
    'ncov_travel',
    'ncov_release',
    'day2',
    'day2_rat',
    'day8',
    'day2and8',
    'tdl_pcr',
    'salient_pcr',
  ].includes(registrationInfo.sku)

  const collectReleaseInfo = ['COV2', 'COV5', 'COV8', 'DAY2_LFD'].includes(testPurpose)
  const collectVaccStatus = collectReleaseInfo

  const hasPaperRegForm = !registrationInfo.sku.startsWith('rapid_')

  return (
    <>
      <h1 className="title has-text-midnightBlue is-spaced">
        Kit <span className="is-family-monospace has-text-weight-light">{toFriendlyKitId(registrationInfo.kitId)}</span>{' '}
        - Register your details
      </h1>

      {hasPaperRegForm && (
        <>
          <p>Please complete the form below with your details so we can correctly identify your sample.</p>

          <p>
            It's important that the details below match the personal details you write on the request form - we can't
            identify your sample otherwise.
          </p>
        </>
      )}

      <br />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema(
          !!registrationInfo.testPartner?.consentRequired,
          collectReleaseInfo,
          registrationInfo.sku
        )}
        onSubmit={async (values, { setFieldError }) => {
          onComplete(values)
        }}
      >
        {(formik) => (
          <Form>
            <p className="mb-2">We are required to collect this data to report to the UK Health Security Agency.</p>

            {collectVaccStatus && (
              <>
                <RadioInput
                  labelClassName="label is-size-6 has-text-weight-light"
                  name="ttr.vaccinationStatus"
                  options={['Vaccinated', 'Unvaccinated']}
                  validate={validateRequired}
                >
                  Coronavirus vaccination status
                </RadioInput>

                {formik.values.ttr.vaccinationStatus === 'Vaccinated' && (
                  <SelectField name="ttr.vaccinationProvider" validate={validateRequired}>
                    <option value="">Select a manufacturer</option>
                    <option>AstraZeneca</option>
                    <option>Pfizer / BioNTech</option>
                    <option>Moderna</option>
                    <option>Johnson & Johnson / Janssen</option>
                    <option>Sinovac</option>
                    <option>Gamaleya Institute/Russian Direct Investment Fund</option>
                    <option>Novavax</option>
                    <option>CureVac</option>
                    <option>Covaxin (Bharat Biotech)</option>
                    <option>Sinopharm Beijing</option>
                  </SelectField>
                )}
              </>
            )}

            <TextInput name="firstName" label="First Name" type="text" autoComplete="off" />

            <TextInput name="lastName" label="Last Name" type="text" autoComplete="off" />

            <DateInput name="dob" />

            {['salient_pcr', 'tdl_pcr'].includes(registrationInfo.sku) && (
              <>
                <SelectField name="sex" label="Sex - as it appears on your passport x" labelClassName="is-size-7">
                  <option value="">Select your sex</option>
                  <option value="Male">M</option>
                  <option value="Female">F</option>
                  <option>X</option>
                </SelectField>

                <TextInput name="nationality" label="Nationality - as it appears on your passport" autoComplete="off" />
              </>
            )}
            <TextInput name="email" label="Email Address" type="email" autoComplete="off" />

            <TextInput name="emailConfirm" type="email" label="Confirm Email Address" />

            {['rapid_travel', 'rapid_travel_hughes'].includes(registrationInfo.sku) && (
              <>
                <RadioInput
                  labelClassName="label is-size-7 has-text-weight-light"
                  name="ttr.homeAddress.location"
                  alwaysShowError={true}
                  options={[
                    {
                      value: 'yes',
                      label: 'Yes',
                    },
                    {
                      value: 'no',
                      label: 'No',
                    },
                  ]}
                >
                  Are you currently in the United Kingdom?
                </RadioInput>

                {formik.values.ttr.homeAddress.location === 'yes' && (
                  <AddressForm name="ttr.homeAddress" labelPrefix="Home" />
                )}
              </>
            )}

            <MicrosoftEmailWarning />

            {registrationInfo.testPartner?.consentRequired && (
              <div className="field">
                <label className="label">Consent to share results</label>
                <p className="mb-3">{parseHtml(registrationInfo.testPartner.consentText)}</p>

                <label className="radio">
                  <Field type="radio" name="sharingConsent" value="true" /> I accept these terms and consent to sharing
                  my results.
                </label>
                <br />
                <label className="radio">
                  <Field type="radio" name="sharingConsent" value="false" /> I do not consent to sharing my results.
                </label>

                <Field name="sharingConsent">
                  {({ meta: { touched, error } }) =>
                    touched && error ? <p className="help is-danger">{error}</p> : null
                  }
                </Field>
              </div>
            )}

            {registrationInfo.sku === 'rapid_multi' && (
              <>
                <RadioInput
                  labelClassName="label is-size-7 has-text-weight-light"
                  name="sex"
                  options={['Male', 'Female']}
                  validate={validateRequired}
                >
                  Sex
                </RadioInput>

                <TextInput name="ethnicity" label="Ethnicity" validate={validateRequired} autoComplete="off" />

                <AddressForm name="homeAddress" labelPrefix="Home " />
              </>
            )}

            {registrationInfo.sku !== 'rapid_multi' && (
              <>
                <DateInput
                  name="sampleDate"
                  chronoInstance={casualGbChrono}
                  label={
                    <>
                      <span>Date you took the sample </span>
                      <span className="has-text-weight-light has-text-info">(e.g. 25/06/2020)</span>
                    </>
                  }
                />

                <div className="field">
                  <label className="label is-size-7 has-text-weight-light">Time you took the sample</label>
                  <div className="select">
                    <Field as="select" name="sampleTimeHours">
                      {range(0, 24).map((hour) => (
                        <option key={hour} value={hour}>
                          {hour.toString().padStart(2, '0')}
                        </option>
                      ))}
                    </Field>
                  </div>
                  <span className="is-size-4">&nbsp;:&nbsp;</span>
                  <div className="select">
                    <Field as="select" name="sampleTimeMinutes">
                      {range(0, 60).map((minute) => (
                        <option key={minute} value={minute}>
                          {minute.toString().padStart(2, '0')}
                        </option>
                      ))}
                    </Field>
                  </div>

                  <Field name="sampleDateTime">
                    {
                      // Not checking 'touched' as it's a synthetic field
                      // set to only show an error if other validations are
                      // met
                      ({ meta: { error } }) => (error ? <p className="help is-danger mb-3">{error}</p> : null)
                    }
                  </Field>
                </div>
              </>
            )}

            {registrationInfo.sku === 'rapid_ag' ||
              (!collectReleaseInfo && (
                <TextInput name="passportNumber" label="Passport /ID Number" type="text" autoComplete="off" />
              ))}

            {showTracking && (
              <TextInput
                name="trackingNumber"
                label={
                  <>
                    <span>Royal Mail Tracking Number </span>
                    <span className="has-text-weight-light has-text-info">
                      (found on the front of your return envelope with PCR test kits)
                    </span>
                  </>
                }
                placeholder="Tracking Number"
                type="text"
                autoComplete="off"
              />
            )}

            {['rapid_travel', 'rapid_travel_hughes'].includes(registrationInfo.sku) && (
              <>
                <AntigenResultInputField
                  validate={validateRequiredWithMessage('You must select a result for your test.')}
                />

                <PhotoUploadField
                  name="photoFile"
                  label="Photo of the test cassette and your passport / ID"
                  previewImgAlt="Your uploaded photo"
                  validate={(value) => {
                    if (value === null) {
                      return 'You must upload a photo of your test cassette and passport / ID'
                    }

                    if (!mimeMatch(value.type, 'image/*')) {
                      return 'Only image files can be uploaded'
                    }

                    return undefined
                  }}
                />

                <hr />
                <div className="field">
                  <label className="checkbox">
                    <Field
                      name="rapidTravelConfirmation"
                      type="checkbox"
                      validate={(val) => {
                        if (val === true) return null

                        return 'You must read and agree with this statement'
                      }}
                    />{' '}
                    I confirm that the individual identified in the photo with this test cassette carried out the test,
                    in accordance with the manufacturer’s instructions, at the time stated.
                  </label>

                  <ErrorMessage name="rapidTravelConfirmation" className="help is-danger" component="p" />
                </div>
              </>
            )}

            {collectReleaseInfo && testPurpose !== 'COV5' && (
              <TextInput
                name="ttr.uniqueId"
                label="Day 2/8 Test Booking Reference Number"
                placeholder="Reference number"
                validate={validateRequired}
                autoComplete="off"
                help="This was emailed to you when you ordered your test."
              />
            )}

            {collectReleaseInfo && <ReleaseInfoSection values={formik.values} />}

            {registrationInfo.testPartner?.registrantNoteHtml && (
              <article className="message is-info mt-5">
                <div className="message-body">{parseHtml(registrationInfo.testPartner.registrantNoteHtml)}</div>
              </article>
            )}

            {registrationInfo.sku === 'salient_pcr' && (
              <>
                {/* Salient do not collect this info for us like TDL did, so ask for it specifically in order to report to PHE */}
                <AddressForm name="ttr.homeAddress" labelPrefix="Home" />

                <TextInput name="ttr.phoneNumber" label="Phone Number" validate={validateRequired} autoComplete="off" />
              </>
            )}

            {formik.errors.__form && <GenericServerError />}

            <div className="buttons is-right mt-5">
              <SubmitButton className="is-midnightBlue" submitting={formik.isSubmitting}>
                Next Step: Review
              </SubmitButton>
            </div>

            {hasErrors(formik) && (
              <div className="is-flex is-justify-content-flex-end is-align-items-center">
                <FaExclamationTriangle className="has-text-danger mr-2" />

                <p className="help is-danger">
                  Some details are missing or incorrect, please scroll up and check on any parts of the form that are
                  highlighted in red.
                </p>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </>
  )
}

export default DetailsForm
